import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';

import {
  useUserVehicleQuery,
  useUserVehicleQueryKeys,
} from '../../../../queries/hooks/UserVehicle.queries';
import { useCarValuationToolContext } from '../../../../CarValuationTool.context';

import {
  type VehicleDetails,
  mileageUnitSchema,
  mileageUnitLowercaseSchema,
} from '../../../../queries/schema/SharedSchema';

import { fireToast } from 'helpers/Toasts';

const useValuationDetails = (args: { vehicleDetails: VehicleDetails }) => {
  const { vehicleDetails } = args;

  const { closeValueCarModal } = useCarValuationToolContext();

  const { usePutUserVehicleQuery } = useUserVehicleQuery();

  const queryClient = useQueryClient();

  const onSuccess = () => {
    closeValueCarModal();
    fireToast({
      type: 'SUCCESS',
      text: 'Your vehicle information has been updated',
    });
    /**
     * The user vehicle data displayed in the Car Valuation Tool may now be considered stale.
     * It is invalidated to trigger a refetch for the updated vehicle details.
     */
    queryClient.invalidateQueries({ queryKey: useUserVehicleQueryKeys.get });
  };

  const {
    mutate,
    isPending,
    isError: isErrorSavingUserVehicle,
    reset,
  } = usePutUserVehicleQuery({
    onSuccess,
  });

  const onClickSave = async () => {
    const mileageUnit =
      vehicleDetails.mileageUnit === mileageUnitSchema.enum.Kilometres
        ? mileageUnitLowercaseSchema.enum.kilometres
        : mileageUnitLowercaseSchema.enum.miles;

    mutate({
      registrationNumber: vehicleDetails.registrationNumber,
      mileage: vehicleDetails.mileage,
      mileageUnit,
    });
  };

  /**
   * For the sake of consistency with the user vehicle details panel,
   * we display the time that the lookup took place
   */
  const valuationDate = `Price for ${dayjs().format('MMMM D, YYYY')}`;

  return {
    onClickSave,
    isPending,
    isErrorSavingUserVehicle,
    valuationDate,
    reset,
  };
};

export { useValuationDetails };
